import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CUSTOM_REACTION_RE,
  customEmojiUrl,
  snowflakeTimestamp,
  type ActionRow,
  type Embed,
  type Poll,
} from '@comms/shared';
import { decryptFile, type EncryptedAttachmentRef } from '@comms/crypto';
import { displayName, roleColor, useStore } from '../store';
import { openModal } from '../store/ui';
import { Markdown } from './Markdown';
import { RichEmbed } from './Embed';
import { MessageComponents } from './MessageComponents';
import { useCommands } from '../store/commands';
import { Avatar, COMMON_EMOJI, Icon, Spinner, cx } from './ui';
import { useIsMobile, useLongPress } from '../hooks/mobile';

export interface ViewAttachment {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  size: number;
  width?: number | null;
  height?: number | null;
  encrypted?: EncryptedAttachmentRef;
  /** play like a GIF: looping, muted, no controls */
  gif?: boolean;
}

export interface ViewMessage {
  id: string;
  authorId: string;
  content: string;
  createdAt: string;
  editedAt: string | null;
  attachments: ViewAttachment[];
  embeds?: Embed[];
  reactions: { emoji: string; count: number; me: boolean }[];
  replyTo?: { id: string; authorId: string; content: string } | null;
  pinned?: boolean;
  system?: string;
  /** an AutoMod alert (shown in the alert channel) */
  automod?: boolean;
  /** sent by a bot or webhook */
  bot?: boolean;
  /** per-message author name (webhooks), shown instead of the user's name */
  authorName?: string | null;
  /** a bot's reply to a slash command someone ran, or to a component someone used */
  interaction?: { name: string; userId: string; type?: 'command' | 'component' } | null;
  /** buttons and select menus (bots) */
  components?: ActionRow[];
  highlight?: boolean;
  undecryptable?: boolean;
  stickers?: { id: string; name: string; url: string }[];
  poll?: Poll | null;
  /** thread started from this message */
  thread?: { id: string; name: string; messageCount: number; archived: boolean } | null;
}

export interface MessageActions {
  onReply?: (m: ViewMessage) => void;
  onEdit?: (m: ViewMessage, content: string) => Promise<void>;
  onDelete?: (m: ViewMessage) => Promise<void>;
  onReact?: (m: ViewMessage, emoji: string, remove: boolean) => void;
  onPin?: (m: ViewMessage, pin: boolean) => void;
  onCreateThread?: (m: ViewMessage) => void;
  onVote?: (m: ViewMessage, answerIds: number[]) => Promise<void>;
  onClosePoll?: (m: ViewMessage) => void;
  /** use a button or select menu on a bot's message */
  onComponent?: (m: ViewMessage, customId: string, label: string, values?: string[]) => void;
  canDeleteAny?: boolean;
  canPin?: boolean;
  canReact?: boolean;
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
const formatSize = (n: number) =>
  n > 1048576
    ? `${(n / 1048576).toFixed(1)} MB`
    : n > 1024
      ? `${(n / 1024).toFixed(0)} KB`
      : `${n} B`;

function useDecryptedUrl(att: ViewAttachment): { url: string | null; error: string | null } {
  const [state, setState] = useState<{ url: string | null; error: string | null }>({
    url: att.encrypted ? null : att.url,
    error: null,
  });
  useEffect(() => {
    if (!att.encrypted) return;
    let revoked = false;
    let objectUrl: string | null = null;
    (async () => {
      const res = await fetch(att.encrypted!.url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('download failed');
      const plain = await decryptFile(new Uint8Array(await res.arrayBuffer()), att.encrypted!);
      objectUrl = URL.createObjectURL(
        new Blob([plain.slice().buffer as ArrayBuffer], { type: att.contentType }),
      );
      if (!revoked) setState({ url: objectUrl, error: null });
    })().catch((err: unknown) =>
      setState({ url: null, error: err instanceof Error ? err.message : 'decryption failed' }),
    );
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [att]);
  return state;
}

function AttachmentView({ att }: { att: ViewAttachment }) {
  const { url, error } = useDecryptedUrl(att);
  const isImage = /^image\/(png|jpeg|webp|gif)$/.test(att.contentType);
  const isVideo = /^video\/(mp4|webm)$/.test(att.contentType);
  const isAudio = /^audio\//.test(att.contentType);
  if (error) return <div className="mt-1 text-sm text-danger">Attachment unavailable: {error}</div>;
  if (!url) return <Spinner className="mt-1" />;
  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 block w-fit max-w-full"
      >
        {/* Never wider than the message column (phones), at most 28rem × 20rem. */}
        <img
          src={url}
          alt={att.filename}
          className="block h-auto max-h-80 w-auto max-w-[min(28rem,100%)] rounded object-contain"
          loading="lazy"
          style={
            att.width && att.height ? { aspectRatio: `${att.width} / ${att.height}` } : undefined
          }
        />
      </a>
    );
  }
  if (isVideo && att.gif)
    return (
      <GifVideo
        src={url}
        title={att.filename.replace(/\.(mp4|webm)$/, '')}
        width={att.width}
        height={att.height}
      />
    );
  if (isVideo)
    return <video src={url} controls className="mt-1 max-h-80 max-w-[min(28rem,100%)] rounded" />;
  if (isAudio) return <audio src={url} controls className="mt-1" />;
  return (
    <a
      href={url}
      download={att.filename}
      className="mt-1 flex w-fit max-w-[min(28rem,100%)] items-center gap-3 rounded border border-bg-0 bg-bg-1 p-3 hover:bg-bg-hover"
    >
      <Icon name="download" className="h-6 w-6 text-text-2" />
      <span className="min-w-0">
        <span className="block truncate text-[#00a8fc]">{att.filename}</span>
        <span className="text-xs text-text-3">{formatSize(att.size)}</span>
      </span>
    </a>
  );
}

/** A GIF is an mp4 that loops silently. */
export function GifVideo({
  src,
  title,
  width,
  height,
  className,
}: {
  src: string;
  title?: string | null;
  width?: number | null;
  height?: number | null;
  className?: string;
}) {
  return (
    <video
      src={src}
      autoPlay
      loop
      muted
      playsInline
      aria-label={title ? `GIF: ${title}` : 'GIF'}
      className={cx('mt-1 max-h-72 max-w-[min(24rem,100%)] rounded', className)}
      style={width && height ? { aspectRatio: `${width} / ${height}` } : undefined}
    />
  );
}

function EmbedView({ embed, guildId }: { embed: Embed; guildId?: string | null }) {
  if (embed.type === 'rich') return <RichEmbed embed={embed} guildId={guildId} />;
  if (embed.type === 'gif')
    return (
      <GifVideo src={embed.url} title={embed.title} width={embed.width} height={embed.height} />
    );
  return (
    <div className="mt-1 max-w-[min(28rem,100%)] rounded border-l-4 border-accent bg-bg-1 p-3">
      {embed.siteName && <div className="text-xs text-text-3">{embed.siteName}</div>}
      {embed.title && (
        <a
          href={embed.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="font-semibold text-[#00a8fc] hover:underline"
        >
          {embed.title}
        </a>
      )}
      {embed.description && <p className="mt-1 text-sm text-text-2">{embed.description}</p>}
      {embed.imageUrl && (
        <img src={embed.imageUrl} alt="" className="mt-2 max-h-60 rounded" loading="lazy" />
      )}
    </div>
  );
}

function timeLeft(iso: string) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'ended';
  const h = Math.floor(ms / 3600_000);
  if (h >= 24) return `${Math.floor(h / 24)}d left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m left`;
}

export function PollView({
  poll,
  canClose,
  onVote,
  onClose,
}: {
  poll: Poll;
  canClose: boolean;
  onVote?: (answerIds: number[]) => Promise<void>;
  onClose?: () => void;
}) {
  const voted = poll.answers.some((a) => a.me);
  const closed = poll.closed || new Date(poll.expiresAt).getTime() <= Date.now();
  const [picked, setPicked] = useState<number[]>([]);
  const showResults = voted || closed || !onVote;
  const totalVotes = poll.answers.reduce((n, a) => n + a.count, 0);
  const top = Math.max(...poll.answers.map((a) => a.count));
  const submit = (ids: number[]) => onVote?.(ids).then(() => setPicked([]));
  return (
    <div className="mt-1 w-full max-w-[min(28rem,100%)] rounded-lg border border-bg-0 bg-bg-1 p-3">
      <div className="mb-2 font-semibold text-text-0">{poll.question}</div>
      <div className="text-xs text-text-3">
        {poll.allowMultiselect ? 'Select one or more answers' : 'Select one answer'}
      </div>
      <div className="mt-2 space-y-1.5" role="group" aria-label="Poll answers">
        {poll.answers.map((a) => {
          const pct = totalVotes ? Math.round((a.count / totalVotes) * 100) : 0;
          const selected = picked.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              disabled={closed || !onVote}
              aria-pressed={showResults ? a.me : selected}
              onClick={() => {
                if (!poll.allowMultiselect) return void submit([a.id]);
                if (voted) {
                  // Already voted: clicking toggles this answer in your vote.
                  const mine = poll.answers.filter((x) => x.me).map((x) => x.id);
                  return void submit(a.me ? mine.filter((x) => x !== a.id) : [...mine, a.id]);
                }
                setPicked(selected ? picked.filter((x) => x !== a.id) : [...picked, a.id]);
              }}
              className={cx(
                'relative flex w-full items-center overflow-hidden rounded border px-3 py-2 text-left text-sm',
                a.me || selected ? 'border-accent' : 'border-bg-3',
                !closed && onVote && 'hover:bg-bg-hover',
              )}
            >
              {showResults && (
                <span
                  className={cx(
                    'absolute inset-y-0 left-0',
                    a.count === top && closed ? 'bg-success/30' : 'bg-accent/20',
                  )}
                  style={{ width: `${pct}%` }}
                />
              )}
              <span className="relative flex-1 text-text-1">{a.text}</span>
              {showResults && (
                <span className="relative text-xs font-semibold text-text-2">
                  {a.count} · {pct}%
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-text-3">
        <span>
          {poll.totalVoters} {poll.totalVoters === 1 ? 'vote' : 'votes'} ·{' '}
          {closed ? 'Poll closed' : timeLeft(poll.expiresAt)}
        </span>
        {!closed && poll.allowMultiselect && !voted && picked.length > 0 && (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => submit(picked)}
          >
            Vote
          </button>
        )}
        {!closed && voted && onVote && (
          <button type="button" className="text-accent hover:underline" onClick={() => submit([])}>
            Remove vote
          </button>
        )}
        {!closed && canClose && onClose && (
          <button type="button" className="ml-auto hover:text-text-1" onClick={onClose}>
            End poll
          </button>
        )}
      </div>
    </div>
  );
}

/** A reaction / picker emoji: unicode, or a custom `name:id`. */
export function EmojiGlyph({ emoji, className }: { emoji: string; className?: string }) {
  const custom = CUSTOM_REACTION_RE.exec(emoji);
  if (!custom) return <span className={className}>{emoji}</span>;
  return (
    <img
      src={customEmojiUrl(custom[2]!)}
      alt={`:${custom[1]}:`}
      title={`:${custom[1]}:`}
      className={cx('inline-block h-[1.2em] w-[1.2em] object-contain', className)}
    />
  );
}

/**
 * Unicode favourites plus custom emoji: the current server's first, then other servers
 * you're in. Picks are `<:name:id>` text for messages and `name:id` for reactions.
 */
export function EmojiPicker({
  onPick,
  onClose,
  guildId,
  mode = 'text',
}: {
  onPick: (e: string) => void;
  onClose: () => void;
  guildId?: string | null;
  mode?: 'text' | 'reaction';
}) {
  const guilds = useStore((s) => s.guilds);
  const order = useStore((s) => s.guildOrder);
  useEffect(() => {
    const t = window.setTimeout(() => window.addEventListener('click', onClose, { once: true }), 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('click', onClose);
    };
  }, [onClose]);
  const sections = [...order]
    .sort((a, b) => (a === guildId ? -1 : b === guildId ? 1 : 0))
    .map((id) => guilds[id])
    .filter((g) => g && g.emojis?.length);
  return (
    <div
      className="z-30 max-h-80 w-72 overflow-y-auto rounded bg-bg-0 p-2 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      {sections.map((g) => (
        <div key={g!.id} className="mb-2">
          <div className="mb-1 truncate text-xs font-bold uppercase text-text-3">{g!.name}</div>
          <div className="grid grid-cols-8 gap-1">
            {g!.emojis.map((e) => (
              <button
                key={e.id}
                type="button"
                title={`:${e.name}:`}
                className="rounded p-1 hover:bg-bg-hover"
                onClick={() =>
                  onPick(
                    mode === 'reaction'
                      ? `${e.name}:${e.id}`
                      : `<${e.animated ? 'a' : ''}:${e.name}:${e.id}>`,
                  )
                }
              >
                <img src={e.url} alt={`:${e.name}:`} className="h-6 w-6 object-contain" />
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="grid grid-cols-8 gap-1">
        {COMMON_EMOJI.map((e) => (
          <button
            key={e}
            type="button"
            className="rounded p-1 text-xl hover:bg-bg-hover"
            onClick={() => onPick(e)}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageRow({
  m,
  compact,
  guildId,
  actions,
}: {
  m: ViewMessage;
  compact: boolean;
  guildId: string | null;
  actions: MessageActions;
}) {
  const meId = useStore((s) => s.me?.id);
  const storedName = useStore((s) => displayName(s, m.authorId, guildId));
  const name = m.authorName ?? storedName;
  const color = useStore((s) => roleColor(s, guildId, m.authorId));
  const replyName = useStore((s) => (m.replyTo ? displayName(s, m.replyTo.authorId, guildId) : ''));
  const invokerName = useStore((s) =>
    m.interaction ? displayName(s, m.interaction.userId, guildId) : '',
  );
  const pendingComponent = useCommands((s) => s.usingComponent[m.id]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.content);
  const [sheet, setSheet] = useState(false);
  const mobile = useIsMobile();
  const actionable = !m.system && !m.undecryptable;
  const press = useLongPress(() => setSheet(true), actionable && !editing);
  const [picker, setPicker] = useState(false);
  const mine = m.authorId === meId;

  const header = (
    <div className="flex items-baseline gap-2">
      <button
        type="button"
        className="font-medium text-text-0 hover:underline"
        style={color ? { color } : undefined}
        onClick={() => openModal({ kind: 'profile', userId: m.authorId, guildId })}
      >
        {name}
      </button>
      {m.bot && (
        <span className="rounded bg-accent px-1 text-[10px] font-bold leading-4 text-white">
          BOT
        </span>
      )}
      {m.automod && (
        <span className="flex items-center gap-1 rounded bg-warning/20 px-1.5 text-xs font-semibold text-warning">
          <Icon name="shield" className="h-3 w-3" /> AutoMod alert
        </span>
      )}
      <span className="text-xs text-text-3">
        {new Date(m.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
      </span>
    </div>
  );

  let body: ReactNode;
  if (m.system) {
    body = <div className="italic text-text-3">{m.system}</div>;
  } else if (m.undecryptable) {
    body = (
      <div className="flex items-center gap-1 text-sm italic text-text-3">
        <Icon name="lock" className="h-4 w-4" /> This message was sent before this device joined the
        conversation and can't be decrypted here.
      </div>
    );
  } else if (editing) {
    body = (
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (draft.trim() && draft !== m.content) await actions.onEdit?.(m, draft.trim());
          setEditing(false);
        }}
      >
        <textarea
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          className="w-full resize-none rounded bg-bg-3 p-2 text-text-1 outline-none"
          rows={Math.min(8, draft.split('\n').length)}
        />
        <div className="text-xs text-text-3">escape to cancel • enter to save</div>
      </form>
    );
  } else {
    body = (
      <>
        {m.content && (
          <div className="text-text-1">
            <Markdown content={m.content} guildId={guildId} />
            {m.editedAt && <span className="ml-1 text-[10px] text-text-3">(edited)</span>}
          </div>
        )}
        {m.attachments.map((a) => (
          <AttachmentView key={a.id} att={a} />
        ))}
        {m.poll && (
          <PollView
            poll={m.poll}
            canClose={mine || !!actions.canDeleteAny}
            onVote={actions.onVote && ((ids) => actions.onVote!(m, ids))}
            onClose={actions.onClosePoll && (() => actions.onClosePoll!(m))}
          />
        )}
        {m.stickers?.map((st) => (
          <img
            key={st.id}
            src={st.url}
            alt={st.name}
            title={st.name}
            className="mt-1 h-40 w-40 object-contain"
          />
        ))}
        {m.embeds?.map((e, i) => (
          <EmbedView key={i} embed={e} guildId={guildId} />
        ))}
        {!!m.components?.length && (
          <MessageComponents
            rows={m.components}
            pending={pendingComponent}
            onUse={
              actions.onComponent
                ? (customId, label, values) => actions.onComponent!(m, customId, label, values)
                : null
            }
          />
        )}
        {m.reactions.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {m.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                disabled={!actions.onReact}
                onClick={() => actions.onReact?.(m, r.emoji, r.me)}
                className={cx(
                  'flex items-center gap-1 rounded-lg border px-1.5 py-0.5 text-sm',
                  r.me
                    ? 'border-accent bg-accent/20'
                    : 'border-transparent bg-bg-1 hover:border-text-3',
                )}
              >
                <EmojiGlyph emoji={r.emoji} />
                <span className="text-xs font-semibold text-text-2">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  }
  const threadChip = m.thread && guildId && (
    <Link
      to={`/channels/${guildId}/${m.thread.id}`}
      className="mt-1 flex w-fit max-w-[min(28rem,100%)] items-center gap-2 rounded border border-bg-0 bg-bg-1 px-3 py-1.5 text-sm hover:bg-bg-hover"
    >
      <Icon name="thread" className="h-4 w-4 text-text-3" />
      <span className="truncate font-semibold text-text-0">{m.thread.name}</span>
      <span className="shrink-0 text-xs text-accent">
        {m.thread.messageCount} {m.thread.messageCount === 1 ? 'message' : 'messages'}
        {m.thread.archived && ' · archived'}
      </span>
    </Link>
  );

  return (
    <div
      {...press}
      data-message-id={m.id}
      className={cx(
        'group relative flex gap-4 px-4 hover:bg-bg-hover/60',
        sheet && 'bg-bg-hover/60',
        compact ? 'py-0.5' : 'mt-3 py-0.5',
        m.highlight && 'border-l-2 border-warning bg-mention',
      )}
    >
      <div className="w-10 shrink-0">
        {compact ? (
          <span className="invisible text-[10px] leading-[1.375rem] text-text-3 group-hover:visible">
            {formatTime(m.createdAt)}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => openModal({ kind: 'profile', userId: m.authorId, guildId })}
          >
            <Avatar userId={m.authorId} />
          </button>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {m.interaction && (
          <div className="mb-0.5 flex items-center gap-1 truncate text-xs text-text-3">
            <Icon name="reply" className="h-3 w-3 -scale-x-100" />
            <span className="font-medium text-text-2">{invokerName}</span>
            {m.interaction.type === 'component' ? (
              'used a button or menu'
            ) : (
              <>
                used <span className="font-medium text-accent">/{m.interaction.name}</span>
              </>
            )}
          </div>
        )}
        {m.replyTo && (
          <div className="mb-0.5 flex items-center gap-1 truncate text-xs text-text-3">
            <Icon name="reply" className="h-3 w-3 -scale-x-100" />
            <span className="font-medium text-text-2">@{replyName}</span>
            <span className="truncate">{m.replyTo.content || 'attachment'}</span>
          </div>
        )}
        {!compact && header}
        {body}
        {threadChip}
      </div>
      {!m.system && !editing && !m.undecryptable && (
        <div className="absolute -top-4 right-4 hidden rounded border border-bg-0 bg-bg-2 shadow group-hover:flex">
          {actions.onReact && actions.canReact !== false && (
            <button
              type="button"
              title="Add reaction"
              className="p-1.5 text-text-2 hover:text-text-0"
              onClick={(e) => (e.stopPropagation(), setPicker(!picker))}
            >
              <Icon name="smile" className="h-4 w-4" />
            </button>
          )}
          {actions.onReply && (
            <button
              type="button"
              title="Reply"
              className="p-1.5 text-text-2 hover:text-text-0"
              onClick={() => actions.onReply!(m)}
            >
              <Icon name="reply" className="h-4 w-4" />
            </button>
          )}
          {actions.onCreateThread && !m.thread && (
            <button
              type="button"
              title="Create thread"
              className="p-1.5 text-text-2 hover:text-text-0"
              onClick={() => actions.onCreateThread!(m)}
            >
              <Icon name="thread" className="h-4 w-4" />
            </button>
          )}
          {mine && actions.onEdit && m.content && (
            <button
              type="button"
              title="Edit"
              className="p-1.5 text-text-2 hover:text-text-0"
              onClick={() => (setDraft(m.content), setEditing(true))}
            >
              <Icon name="edit" className="h-4 w-4" />
            </button>
          )}
          {actions.onPin && actions.canPin && (
            <button
              type="button"
              title={m.pinned ? 'Unpin' : 'Pin'}
              className="p-1.5 text-text-2 hover:text-text-0"
              onClick={() => actions.onPin!(m, !m.pinned)}
            >
              <Icon name="pin" className="h-4 w-4" />
            </button>
          )}
          {(mine || actions.canDeleteAny) && actions.onDelete && (
            <button
              type="button"
              title="Delete"
              className="p-1.5 text-danger hover:brightness-125"
              onClick={(e) => {
                if (e.shiftKey || confirm('Delete this message?')) void actions.onDelete!(m);
              }}
            >
              <Icon name="trash" className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      {sheet && (
        <MessageActionSheet
          m={m}
          mine={mine}
          actions={actions}
          onClose={() => setSheet(false)}
          onMoreReactions={() => setPicker(true)}
          onEdit={() => (setDraft(m.content), setEditing(true))}
        />
      )}
      {picker && (
        <div
          className={
            mobile
              ? 'safe-bottom fixed inset-x-0 bottom-0 z-50 flex justify-center bg-black/40 pt-24'
              : 'absolute right-4 top-6 z-30'
          }
        >
          <EmojiPicker
            guildId={guildId}
            mode="reaction"
            onClose={() => setPicker(false)}
            onPick={(e) => {
              setPicker(false);
              const existing = m.reactions.find((r) => r.emoji === e);
              actions.onReact?.(m, e, !!existing?.me);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Touch: what the hover toolbar offers, as a bottom sheet (opened by long-pressing a message). */
function MessageActionSheet({
  m,
  mine,
  actions,
  onClose,
  onMoreReactions,
  onEdit,
}: {
  m: ViewMessage;
  mine: boolean;
  actions: MessageActions;
  onClose: () => void;
  onMoreReactions: () => void;
  onEdit: () => void;
}) {
  const run = (fn: () => void) => () => {
    onClose();
    fn();
  };
  const item = (
    label: string,
    icon: Parameters<typeof Icon>[0]['name'],
    fn: () => void,
    danger?: boolean,
  ) => (
    <button
      type="button"
      onClick={run(fn)}
      className={cx(
        'flex w-full items-center gap-3 rounded px-3 py-3 text-left',
        danger ? 'text-danger' : 'text-text-0',
        'active:bg-bg-hover',
      )}
    >
      <Icon name={icon} className="h-5 w-5" />
      {label}
    </button>
  );
  const canReact = actions.onReact && actions.canReact !== false;
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Message actions"
        className="safe-bottom w-full rounded-t-2xl bg-bg-1 p-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-bg-3" />
        {canReact && (
          <div className="mb-2 flex justify-between gap-1">
            {COMMON_EMOJI.slice(0, 6).map((e) => (
              <button
                key={e}
                type="button"
                aria-label={`React with ${e}`}
                onClick={run(() =>
                  actions.onReact!(m, e, !!m.reactions.find((r) => r.emoji === e)?.me),
                )}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-bg-2 text-2xl active:bg-bg-hover"
              >
                {e}
              </button>
            ))}
            <button
              type="button"
              aria-label="More reactions"
              onClick={run(onMoreReactions)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-bg-2 text-text-2"
            >
              <Icon name="smile" />
            </button>
          </div>
        )}
        {actions.onReply && item('Reply', 'reply', () => actions.onReply!(m))}
        {actions.onCreateThread &&
          !m.thread &&
          item('Create thread', 'thread', () => actions.onCreateThread!(m))}
        {m.content &&
          item('Copy text', 'message', () => void navigator.clipboard?.writeText(m.content))}
        {mine && actions.onEdit && m.content && item('Edit message', 'edit', onEdit)}
        {actions.onPin &&
          actions.canPin &&
          item(m.pinned ? 'Unpin' : 'Pin message', 'pin', () => actions.onPin!(m, !m.pinned))}
        {(mine || actions.canDeleteAny) &&
          actions.onDelete &&
          item(
            'Delete message',
            'trash',
            () => confirm('Delete this message?') && void actions.onDelete!(m),
            true,
          )}
      </div>
    </div>
  );
}

export function MessageList({
  messages,
  guildId,
  actions,
  hasMore,
  loadOlder,
  intro,
  footer,
  footerCount = 0,
}: {
  messages: ViewMessage[];
  guildId: string | null;
  actions: MessageActions;
  hasMore: boolean;
  loadOlder?: () => Promise<void>;
  intro: ReactNode;
  /** shown after the last message (e.g. command replies only you can see) */
  footer?: ReactNode;
  /** changes when the footer grows, to keep the view scrolled to the bottom */
  footerCount?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const prevHeight = useRef(0);
  const loading = useRef(false);
  const firstId = messages[0]?.id;

  // Keep the viewport pinned to the bottom unless the user scrolled up; keep position when prepending.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prevHeight.current && el.scrollTop < 50)
      el.scrollTop = el.scrollHeight - prevHeight.current;
    else if (atBottom.current) el.scrollTop = el.scrollHeight;
    prevHeight.current = 0;
  }, [messages.length, firstId, footerCount]);

  async function onScroll() {
    const el = ref.current!;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 200 && hasMore && loadOlder && !loading.current) {
      loading.current = true;
      prevHeight.current = el.scrollHeight;
      await loadOlder().finally(() => (loading.current = false));
    }
  }

  return (
    <div ref={ref} onScroll={() => void onScroll()} className="flex-1 overflow-y-auto pb-4">
      {!hasMore && <div className="px-4 pb-4 pt-12">{intro}</div>}
      {hasMore && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}
      {messages.map((m, i) => {
        const prev = messages[i - 1];
        const newDay =
          !prev || new Date(prev.createdAt).toDateString() !== new Date(m.createdAt).toDateString();
        const compact =
          !!prev &&
          !newDay &&
          !m.system &&
          !prev.system &&
          !m.automod &&
          !prev.automod &&
          !m.replyTo &&
          !m.interaction &&
          prev.authorId === m.authorId &&
          prev.authorName === m.authorName &&
          new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 7 * 60_000;
        return (
          <Fragment key={m.id}>
            {newDay && (
              <div className="mx-4 my-4 flex items-center gap-2 text-xs font-semibold text-text-3">
                <span className="h-px flex-1 bg-bg-3" />
                {formatDate(m.createdAt)}
                <span className="h-px flex-1 bg-bg-3" />
              </div>
            )}
            <MessageRow m={m} compact={compact} guildId={guildId} actions={actions} />
          </Fragment>
        );
      })}
      {footer}
    </div>
  );
}

export const idTime = (id: string) => new Date(snowflakeTimestamp(id)).toISOString();
